import * as THREE from 'three';
import { loadModel } from './models.js';

// Hand-made landmark models (public/assets/models/landmarks.json) placed at their real
// coordinates, in place of the plain building the grammar would build from the map footprint.
// The list is read once; `skips` tells both streaming paths which map buildings to leave out.

export class Landmarks {
  constructor(scene, heightmap, regionId) {
    this.scene = scene;
    this.heightmap = heightmap;
    this.regionId = regionId;
    this.list = [];
    this.skips = {}; // tile key -> [building indices the grammar must skip]
    this.meshes = [];
    this.material = new THREE.MeshLambertMaterial({ vertexColors: true });
    this.ready = this.load();
  }

  async load() {
    try {
      const res = await fetch('assets/models/landmarks.json');
      if (!res.ok) return;
      const json = await res.json();
      this.list = (json.landmarks || []).filter((l) => l.region === this.regionId);
      for (const l of this.list) {
        for (const s of l.skip || []) {
          if (!this.skips[s.tile]) this.skips[s.tile] = [];
          this.skips[s.tile].push(s.index);
        }
      }
      await Promise.all(this.list.map((l) => this.place(l)));
    } catch (e) {
      console.warn('landmarks did not load', e);
    }
  }

  async place(l) {
    const geometry = await loadModel(l.model, { scale: l.scale || 1, onGround: true });
    if (!geometry) return;
    const mesh = new THREE.Mesh(geometry, this.material);
    // the plinth sits a little into the ground on the uphill side; the heightmap is sampled at the middle
    mesh.position.set(l.x, this.heightmap.heightAt(l.x, l.z) - 0.2, l.z);
    mesh.rotation.y = l.yaw || 0;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    this.scene.add(mesh);
    this.meshes.push(mesh);
  }

  // True if a circle on the ground meets a landmark's extent (its bounding sphere seen from
  // above): where market sheds and other scattered things must not stand.
  blocks(x, z, r) {
    for (const m of this.meshes) {
      const s = m.geometry.boundingSphere;
      if (!s) continue;
      const reach = s.radius * (m.scale.x || 1) + r;
      if ((m.position.x - x) ** 2 + (m.position.z - z) ** 2 < reach * reach) return true;
    }
    return false;
  }
}
